import {
  BaseQueryApi,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { isString } from 'lodash';
import { ENV } from '~/config';
import { RootState } from '~/redux';
import { setAuthError } from '~/redux/slices/authSlice';

export type ApiErrorDetail = {
  message?: string;
};

export type ApiErrorData = {
  detail?: ApiErrorDetail | string;
};

export type ApiErrorResponse = FetchBaseQueryError & {
  data?: ApiErrorData | unknown;
};

const isUserMeRequest = (args: string | FetchArgs): boolean => {
  const url = isString(args) ? args : args.url;

  return url === 'users/me';
};

const AUTH_ERROR_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to access this resource.',
  404: 'The requested resource was not found.',
};

const getAuthErrorMessage = (status: number): string =>
  AUTH_ERROR_MESSAGES[status] ?? 'User is not authorized to access this application';

const fetchBaseQueryWithTokenCheck = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  baseUrl: string | undefined,
  extraOptions: object,
) => {
  const state = api.getState() as RootState;
  const token = state.userSession.idToken;

  const result = await fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      if (!headers.has('Content-Type')) {
        const requestArgs = isString(args) ? { url: args } : args;
        const isFormData = requestArgs.body instanceof FormData;

        if (!isFormData) {
          headers.set('Content-Type', 'application/json');
        }
      }

      return headers;
    },
  })(args, api, extraOptions);

  const errorStatus = result.error?.status;

  if (typeof errorStatus === 'number' && errorStatus >= 400 && errorStatus < 500 && isUserMeRequest(args)) {
    api.dispatch(setAuthError({ message: getAuthErrorMessage(errorStatus), status: errorStatus }));
  }

  return result;
};

const fetchBaseQueryWrapper: (
  args: string | FetchArgs,
  api: BaseQueryApi,
) => Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>> = (
  args: string | FetchArgs,
  api: BaseQueryApi,
) => {
  const apiUrl = ENV.VITE_ROOT_API_URL;

  return fetchBaseQueryWithTokenCheck(args, api, apiUrl, {});
};

export const v2Api = createApi({
  reducerPath: 'v2Api',
  baseQuery: fetchBaseQueryWrapper,
  endpoints: () => ({}),
});
